/**
 * hs-personalizer — the engraving field, and the live preview on /personalize.
 *
 * Two custom elements, one module:
 *
 *   <hs-personalizer>          the product-page field: hard character cap, a
 *                              live n / max counter, and the final-sale
 *                              notice that appears with the first letter
 *   <hs-personalizer-preview>  the Personalize page: the same field driving a
 *                              debossed-into-leather preview
 *
 * The value is capital A–Z only: the stamp set holds nothing else, and the cap
 * and the character set are both enforced here as well as by the maxlength and
 * pattern attributes. Neither attribute reliably stops a paste in every
 * browser, and this value goes onto a real order and then onto leather with a
 * hot stamp — there is no undo.
 *
 * Both elements work as plain inputs with JavaScript off; only the counter and
 * the preview are lost.
 */

/**
 * Uppercase, reduce to A–Z, and clamp to the cap.
 *
 * Accents are folded onto their base letter rather than dropped, so typing É
 * leaves an E behind instead of swallowing the keystroke. Everything else a
 * hot stamp cannot set — digits, punctuation, spaces, emoji — is removed.
 */
function clean(value, max = Infinity) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, max);
}

/** Where the caret belongs once `clean` has run: the kept characters before it. */
function caretAfterClean(value, caret, max) {
  return clean(value.slice(0, caret), max).length;
}

class HSPersonalizer extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-hs-engrave-input]');
    this.counter = this.querySelector('[data-hs-engrave-counter]');
    this.status = this.querySelector('[data-hs-engrave-status]');
    this.notice = this.querySelector('[data-hs-engrave-notice]');
    if (!this.input) return;

    this.max = parseInt(this.dataset.max, 10) || 12;
    this.rejected = this.dataset.rejectedMessage || 'Capital letters A–Z only.';
    this.onInput = this.onInput.bind(this);
    this.input.addEventListener('input', this.onInput);
    // onInput, not update: a value the browser restored on a back-navigation
    // has never been through clean().
    this.onInput();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
  }

  onInput() {
    const raw = this.input.value;
    const kept = clean(raw);
    const cleaned = kept.slice(0, this.max);
    if (cleaned !== raw) {
      // Keep the caret where the typist left it after a rejected or truncated paste.
      const at = caretAfterClean(raw, this.input.selectionStart ?? raw.length, this.max);
      this.input.value = cleaned;
      this.input.setSelectionRange(at, at);
    }
    // Shorter than the raw value means characters were refused, not merely
    // truncated — worth saying out loud, since nothing appears on screen.
    this.update(kept.length !== raw.length);
  }

  update(refused) {
    const used = this.input.value.length;
    if (this.counter) this.counter.textContent = `${used} / ${this.max}`;
    // The final-sale notice: shown as soon as anything is embossed, gone when
    // the field is cleared. role="status" reads it out once, when it appears.
    this.notice?.toggleAttribute('hidden', used === 0);

    if (this.status) {
      // One region, one message: the refusal first, then the cap, then silence.
      let message = '';
      if (refused) message = this.rejected;
      else if (used === this.max) message = `${used} / ${this.max}`;
      if (message !== this.status.textContent) this.status.textContent = message;
    }

    this.dispatchEvent(
      new CustomEvent('hs:engraving-change', {
        bubbles: true,
        detail: { value: this.input.value, remaining: this.max - used },
      })
    );
  }
}

class HSPersonalizerPreview extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-hs-preview-input]');
    this.output = this.querySelector('[data-hs-preview-output]');
    this.counter = this.querySelector('[data-hs-preview-counter]');
    if (!this.input || !this.output) return;

    this.max = parseInt(this.dataset.max, 10) || 12;
    // Never show an empty preview — the fallback is the point of the page.
    this.fallback = this.dataset.fallback || 'AMR';
    this.onInput = this.onInput.bind(this);
    this.input.addEventListener('input', this.onInput);
    this.onInput();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
  }

  onInput() {
    const raw = this.input.value;
    const cleaned = clean(raw, this.max);
    if (cleaned !== raw) {
      const at = caretAfterClean(raw, this.input.selectionStart ?? raw.length, this.max);
      this.input.value = cleaned;
      this.input.setSelectionRange(at, at);
    }
    this.render();
  }

  render() {
    // Already A–Z from `clean`; the fallback is the merchant's, shown as set.
    this.output.textContent = this.input.value || this.fallback;
    if (this.counter) this.counter.textContent = `${this.input.value.length} / ${this.max}`;
  }
}

if (!customElements.get('hs-personalizer')) {
  customElements.define('hs-personalizer', HSPersonalizer);
}
if (!customElements.get('hs-personalizer-preview')) {
  customElements.define('hs-personalizer-preview', HSPersonalizerPreview);
}
